import React from 'react';
import Modal from '../Modal/Modal';
import { CycleController } from '../../store/models/controllers';
import { useFormik } from 'formik';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { useTranslation } from 'react-i18next';
import { ICustomChannelName } from '../../types/channel-types';
import Table from '../Table/Table';

import './ReportChannelsModal.css';
import HorizontalAlignmentDirections from '../../enums/horizontal-alignment-directions.enum';
import { CycleReportItem } from '../../store/models/reports';
import { ControllerItemType } from '../../enums/controller-item-type';

interface FormValues {
  items: CycleReportItem[]
}

type TableColumn = {
  name: string,
  isIncluded: string,
  color: string,
}

type TableHeadAlignment = {
  [key in TableColumn[keyof TableColumn]]: HorizontalAlignmentDirections;
};

interface TReportChannelsModalProps {
  channelNames: ICustomChannelName[];
  cycleControllers: CycleController[],
  cycleReportItems: CycleReportItem[],
  fetchData: () => Promise<void>;
  onClose: () => void;
  updateData: (items: { id: string, color: string, excluded: boolean }[]) => Promise<void>;
};

const ReportChannelsModalV2: React.FC<TReportChannelsModalProps> = ({
  channelNames,
  cycleControllers,
  cycleReportItems,
  fetchData,
  onClose,
  updateData,
}) => {
  const { t } = useTranslation();

  const tableColumn: TableColumn = {
    name: t('commonText.name'),
    isIncluded: t('commonText.isIncluded'),
    color: t('commonText.color'),
  }
  const tableHeadAlignment: TableHeadAlignment = {
    [tableColumn.name]: HorizontalAlignmentDirections.Left,
    [tableColumn.isIncluded]: HorizontalAlignmentDirections.Center,
    [tableColumn.color]: HorizontalAlignmentDirections.Center,
  };
  const tableHeads = Object.values(tableColumn);

  const controllerNamesById = cycleControllers.reduce((accumulator, cycleController) => {
    const { id, name, serial_number } = cycleController.controller;
    accumulator[id] = name || serial_number;

    return accumulator;
  }, {});

  const cycleReportItemsFormik = useFormik<FormValues>({
    initialValues: {
      items: cycleReportItems,
    },
    onSubmit: async ({ items }) => {
      const payload = items.map(({ id, excluded, color }) => ({ id, excluded, color }))

      await updateData(payload);
      await fetchData();
      onClose();
    },
  });

  const generateOriginalName = (deviceType: ControllerItemType, device_num: number, controllerName: string) =>
    `${deviceType}-${device_num} (${controllerName})`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const { checked, type, value } = event.target;

    let items: CycleReportItem[] = cycleReportItemsFormik.values.items;

    if (type === 'checkbox') {
      items = items.map((item) => item.id === id ? ({ ...item, excluded: !checked }) : item);
    }

    if (type === 'color') {
      items = items.map((item) => item.id === id ? ({ ...item, color: value }) : item);
    }

    cycleReportItemsFormik.setFieldValue('items', items);
  };

  const getValueByItem = (item: CycleReportItem) => {
    const originalName = generateOriginalName(ControllerItemType.CHANNEL, item.itemNumber, controllerNamesById[item.controllerId]);
    const tableName =
      channelNames.find((n) => n.original === originalName)?.fromDynamo || originalName;

    return {
      id: item.id,
      [tableColumn.name]: (<span className='inline-block w-full text-left'>{tableName}</span>),
      [tableColumn.isIncluded]: (
        <span className='flex justify-center items-center'>
          <input
            className="h-5 w-5"
            type="checkbox"
            checked={!item.excluded}
            onChange={(e) => handleChange(e, item.id)}
          />
        </span>
      ),
      [tableColumn.color]: (
        <span className='flex justify-center items-center rounded'>
          <span className='inline-block rounded w-5 h-5 border-2 border-solid overflow-hidden'>
            <input
              className="cursor-pointer relative inline-block"
              type="color"
              value={item.color}
              onChange={(e) => handleChange(e, item.id)}
            />
          </span>
        </span>
      )
    };
  }

  const tableValues = cycleReportItemsFormik.values.items.map((item) => getValueByItem(item));

  const submitFormByBtn = (e: React.MouseEvent) => {
    e.preventDefault();
    cycleReportItemsFormik.submitForm();
  };

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <Modal
      modalTitle={t('labels.chartSettings')}
      isVisible={true}
      onClose={onClose}
      showAcceptBtn={false}
      showCancelBtn={false}
      className='sm:min-w-fit'
    >
      <form onSubmit={submitForm} className="c-report-channels">
        <Table values={tableValues} keyAlign={tableHeadAlignment} defaultKeys={tableHeads} />
        <InteractiveButton
          text={t('buttons.save')}
          submit
          onClick={submitFormByBtn}
          isAdaptive
          className="w-full mt-4"
        />
      </form>
    </Modal>
  )
};

export default ReportChannelsModalV2;
